import { ServerEvent } from "@/common/notification";
import {
	CallSessionActions,
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { CallSession, CallType } from "@/common/models/callSession";
import { useAuth } from "@/hooks/authentication";
import { notificationListener } from "@/hooks/notification";
import { fetchCurrentCallSession } from "@/repository/queryFns";
import { useCallback, useEffect, useRef } from "react";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import { useIntl } from "react-intl";
import {
	AppCacheActions,
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { useMessage } from "@/common/messages";
import {
	NavigationActions,
	NavigationState,
	TabType,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { AutoOpenTrigger } from "@/repository/models";
import { openResourcesPopup } from "@/common/helpers/popupHelper";
import {
	QueuesWithResourcesActions,
	QueuesWithResourcesState,
	useQueuesWithResourcesStore,
} from "@/common/stores/nonpersistingStores/queues";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { InteractionType } from "@/common/types";
import { cleanupResourcesCache } from "@/common/helpers";
import { useResponsiveBreakpoints } from "@/hooks/responsive";

export const useCallSessionManager = () => {
	const authContext = useAuth();
	const intl = useIntl();
	const { show: showMessage } = useMessage();
	const lastTerminatedCallIdRef = useRef<string | undefined>(undefined);

	const updateCallSession = useCallSessionStore(
		(actions: CallSessionActions) => actions.update
	);
	const callSessionState = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const selectedTab = useNavigationStore(
		(state: NavigationState) => state.selectedTab
	);

	const activeInteractionsCount = useNavigationStore(
		(state: NavigationState) => state.activeInteractionsCount
	);

	const setSelectedTab = useNavigationStore(
		(state: NavigationActions) => state.setSelectedTab
	);
	const setToggledTab = useNavigationStore(
		(state: NavigationActions) => state.setToggledTab
	);
	const setIsMobileDetailViewMode = useNavigationStore(
		(actions: NavigationActions) => actions.setIsMobileDetailViewMode
	);

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);
	const queueWithResourcesStore = useQueuesWithResourcesStore(
		(store: QueuesWithResourcesState & QueuesWithResourcesActions) => store
	);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const queryClient = useQueryClient();

	const { isMobile } = useResponsiveBreakpoints();

	useEffect(() => {
		if (!authContext.isLogged) {
			return;
		}

		fetchCurrentCallSession().then((callSession: CallSession) => {
			if (
				currentInteraction &&
				currentInteraction.type == InteractionType.Call &&
				callSession?.id !== currentInteraction.id
			) {
				if (callSession) {
					updateCallSession(null); // for unify current interaction could be a secondary call
				}

				return;
			}

			if (!callSession) {
				return;
			}

			// Avoid overwriting a more advanced state (e.g. Active) with a stale Offering state
			// that arrives via HTTP response after the CallSessionStateChanged notification already updated the store.
			const currentStoreSession = useCallSessionStore.getState().callSession;
			if (
				currentStoreSession?.id === callSession.id &&
				callSession.state === AgentInteractionStatus.Offering &&
				currentStoreSession?.state !== AgentInteractionStatus.Offering
			) {
				handleAcceptedCallSession(callSession);
				setCallInProgress(true);
				cleanupResourcesCache(callSession, appStoreActions);
				return;
			}

			setSessionVariableText(callSession);
			updateCallSession(callSession);
			handleAcceptedCallSession(callSession);
			setCallInProgress(true);
			cleanupResourcesCache(callSession, appStoreActions);
		});
	}, [authContext.isLogged, currentInteraction]);

	notificationListener(
		ServerEvent.CallSessionStateChanged,
		(callSession: CallSession) => {
			if (callSession.state === AgentInteractionStatus.Terminated && !(callSession.isCallIgnored || callSession.isCallParked)) {
				lastTerminatedCallIdRef.current = callSession.id;
			}

			if (
				callSession.state === AgentInteractionStatus.Offering && callSession.callType === CallType.Outgoing &&
				lastTerminatedCallIdRef.current === callSession.id
			) {
				return;
			}

			// An MS Teams toaster offer has no currentAgent yet (the agent is only being rung) and
			// isSimultaneousOffering stays false so the in-client accept/reject card is not raised — without
			// this exception the session would be discarded here and the interaction details card shown beside
			// the native Teams toast would have nothing to render from.
			if (
				!callSession.isSimultaneousOffering &&
				!callSession.isMsTeamsToasterOffering &&
				(!callSession.currentAgent ||
					(callSession.state === AgentInteractionStatus.MonitoringEnded &&
						callSession.supervisorId === authContext.userId))
			) {
				updateCallSession(null);

				const currentInteraction = appStoreCache[CacheKey.CurrentInteractionId];

				if (currentInteraction === callSession.id) {
					appStoreActions.removeCaches([CacheKey.CurrentInteractionId]);
				}

				appStoreActions.removeCaches([
					CacheKey.OfferingCallId,
					CacheKey.OfferingDynamicUrlCallId,
					CacheKey.AcceptedDynamicUrlCallId,
				]);
				setCallInProgress(false);
			} else {
				// isMsTeamsToasterOffering is computed per recipient and the notification only goes to the
				// offered agents, so it already means "I am the one being rung" — simultaneousOfferingAgentIds
				// is deliberately null for this method.
				if (
					callSession.currentAgent?.id == authContext.userId ||
					callSession.simultaneousOfferingAgentIds?.includes(authContext.userId) ||
					callSession.isMsTeamsToasterOffering
				) {
					setSessionVariableText(callSession);
					updateCallSession(callSession);
					setCurrentInteractionId(callSession);
					setOnHoldStatus(callSession);
				}

				handleAcceptedCallSession(callSession);

				if (isMobile) {
					setIsMobileDetailViewMode(true);
					setToggledTab();
				}

				if (callSession.state === AgentInteractionStatus.Terminated) {
					setCallInProgress(false);

					appStoreActions.removeCaches([CacheKey.OfferingDynamicUrlCallId]);
				}
			}

			showMessageBasedOnTheState(callSession);
			refetchActiveForMonitoring(callSession);
		}
	);

	const setSessionVariableText = (callSession: CallSession) => {
		if (!callSession.activeVariableText) {
			return;
		}

		appStoreActions.setCache(
			CacheKey.SessionVariableText,
			callSession.activeVariableText
		);
	};

	const handleAcceptedCallSession = (callSession: CallSession) => {
		const { isAcceptedCall, hasCallerLeft } = callSession;
		const offeringCallId = appStoreCache[CacheKey.OfferingCallId];

		if (isAcceptedCall && !hasCallerLeft && offeringCallId) {
			if (activeInteractionsCount <= 1) {
				appStoreActions.setCache(CacheKey.PreviousTab, selectedTab);
			}

			setSelectedTab(TabType.Interactions);

			setCallInProgress(true);
		}
	};

	useEffect(() => {
		if (!callSessionState || !queueWithResourcesStore.queues?.length) {
			return;
		}
		const {
			isAcceptedCall,
			hasCallerLeft,
			state,
			id,
			queueId,
			isCallbackRegistered,
		} = callSessionState;
		const isWarmTransferInvitation =
			state === AgentInteractionStatus.ReceivingWarmTransferInvitation;
		const acceptedDynamicUrlCallId =
			appStoreCache[CacheKey.AcceptedDynamicUrlCallId];

		if (
			isAcceptedCall &&
			!hasCallerLeft &&
			!isWarmTransferInvitation &&
			acceptedDynamicUrlCallId !== id &&
			!isCallbackRegistered
		) {
			appStoreActions.setCache(CacheKey.AcceptedDynamicUrlCallId, id);

			const queue = queueWithResourcesStore.getQueueById(queueId);
			openResourcesPopup(queue, AutoOpenTrigger.WhenHandlingAccepted);
		}
	}, [callSessionState, queueWithResourcesStore.queues]);

	const showMessageBasedOnTheState = useCallback(
		(callSession: CallSession) => {
			switch (callSession?.state) {
				case AgentInteractionStatus.WarmTransferInviteRejected:
					showMessage("workspace", {
						intent: "error",
						text: intl.formatMessage({ id: "Transfer.WarmInviteRejected" }),
					});
					break;

				case AgentInteractionStatus.CalleeUnavailable:
					showMessage("workspace", {
						intent: "error",
						text: intl.formatMessage({ id: "ErrorMessage.Unavailable" }),
					});
					break;

				case AgentInteractionStatus.Terminated:
					if (callSession.hasCallerLeft && !callSession.isCallbackRegistered) {
						showMessage("workspace", {
							intent: "error",
							text: intl.formatMessage({
								id: "Interaction.Messages.CallerLeft",
							}),
						});
					}
					appStoreActions.removeCaches([CacheKey.OnHoldStatus]);
					break;

				case AgentInteractionStatus.CallbackRegistered:
					showMessage("workspace", {
						intent: "error",
						text: intl.formatMessage({
							id: "Interaction.Messages.CallbackRegistered",
						}),
					});
					appStoreActions.removeCaches([CacheKey.OnHoldStatus]);
					break;

				case AgentInteractionStatus.SimultaneousOfferingTerminated:
					showMessage("workspace", {
						intent: "error",
						text: intl.formatMessage({
							id: "Interaction.Messages.CallAcceptedByAnotherAgent",
						}),
					});
					break;

				case AgentInteractionStatus.SimultaneousOfferingCallerLeft:
					showMessage("workspace", {
						intent: "error",
						text: intl.formatMessage({
							id: "Interaction.Messages.CallerLeft",
						}),
					});
					break;
			}
		},
		[showMessage]
	);

	const setCurrentInteractionId = useCallback((callSession: CallSession) => {
		switch (callSession?.state) {
			case AgentInteractionStatus.Offering:
			case AgentInteractionStatus.MonitoringStarting:
			case AgentInteractionStatus.ReceivingWarmTransferInvitation:
			case AgentInteractionStatus.SimultaneousOffering:
				appStoreActions.setCache(CacheKey.OfferingCallId, callSession.id);
				break;
			case AgentInteractionStatus.Rejected:
			case AgentInteractionStatus.OutboundAcceptedByAgent:
				appStoreActions.removeCaches([CacheKey.OfferingCallId]);
				break;
			case AgentInteractionStatus.Terminated:
			case AgentInteractionStatus.SimultaneousOfferingTerminated:
				var currentInteraction = appStoreCache[CacheKey.CurrentInteractionId];

				if (currentInteraction === callSession.id) {
					appStoreActions.removeCaches([CacheKey.CurrentInteractionId]);
				}

				setCallInProgress(false);
				break;
			case AgentInteractionStatus.AcceptedWarmTransferInvitation:
			case AgentInteractionStatus.WarmTransferInviteAccepted:
			case AgentInteractionStatus.WarmTransferInviteRejected:
			case AgentInteractionStatus.WarmTransferConsultingAgent:
			case AgentInteractionStatus.WarmTransferConsultingCaller:
			case AgentInteractionStatus.WarmCanceled:
			case AgentInteractionStatus.WarmCanceledBySecondAgent:
			case AgentInteractionStatus.CalleeUnavailable:
				appStoreActions.setCache(CacheKey.CurrentInteractionId, callSession.id);
				break;
		}
	}, []);

	const setOnHoldStatus = useCallback((callSession: CallSession) => {
		switch (callSession?.state) {
			case AgentInteractionStatus.Offering:
			case AgentInteractionStatus.Resumed:
				appStoreActions.setCache(CacheKey.OnHoldStatus, false);
				break;
			case AgentInteractionStatus.WarmTransferInviteRejected:
			case AgentInteractionStatus.WarmCanceledBySecondAgent:
			case AgentInteractionStatus.WarmCanceled:
				appStoreActions.setCache(
					CacheKey.OnHoldStatus,
					!callSession?.wasWarmTransferConferenceMode
				);
				break;
		}
	}, []);

	const refetchActiveForMonitoring = useCallback((callSession: CallSession) => {
		if (callSession.state === AgentInteractionStatus.MonitoringEnded) {
			queryClient.invalidateQueries({
				queryKey: ["supervisorActiveInteractions"],
			});
			queryClient.invalidateQueries({ queryKey: ["interactions"] });
		}
	}, []);

	const setCallInProgress = (callInProgress: boolean) =>
		appStoreActions.setCache(CacheKey.CallInProgress, callInProgress);
};
